import {
  useBulkValidatePaperScreeningsApi,
  useCreateAbstractScreening,
  useCreateAndExecuteLiteratureReviewSearches,
  useCreateCriteriaApi,
  useCreateLiteratureReview,
  useDeleteCriteriaApi,
  useDeleteLiteratureSearchApi,
  useExecuteAbstractScreening,
  useExecuteFullTextScreeningApi,
  useExportFullTextScreeningResultsApi,
  useExportLiteratureReviewApi,
  useExportLiteratureSearchesApi,
  useGenerateCriteriaApi,
  useGenerateFullTextCriteriaApi,
  useGetAbstractScreeningByIdApi,
  useGetAbstractScreeningResultsByIdApi,
  useGetFullTextScreeningApi,
  useGetFullTextScreeningResultsApi,
  useGetLiteratureReview,
  useGetLiteratureSearchesApi,
  useGetSearchResultsApi,
  useListLiteratureReviews,
  useLiteratureReviewSearchesApi,
  useSearchPapers,
  useUpdateCriteriaApi,
  useUpdateFullTextScreeningCriteriaResultApi,
  useUpdateLiteratureReview,
  useUpdatePaperScreeningResultApi,
  useUpdatePaperScreeningResultJustificationApi,
  useUpdatePaperScreeningValidationStatusApi,
  useUploadWorkPdfApi,
  useUpsertAndExecuteLiteratureReviewSearchesApi,
  useUpsertFullTextScreeningApi,
} from '@/api/literatureReviews';
import { browserDownload } from '@/utils/downloadFileUtils';
import {
  ExportLiteratureSearchesResponse,
  GetAbstractScreeningResultsFilterParams,
  GetFullTextScreeningResultsFilterParams,
} from '@ditto/shared';
import { MutateOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

export const useLiteratureReviewsService = () => {
  const { data, ...rest } = useListLiteratureReviews();

  const literatureReviews = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { literatureReviews, ...rest };
};

export const useGetLiteratureReviewService = (
  literatureReviewId: string | undefined
) => {
  const { data, ...rest } = useGetLiteratureReview(literatureReviewId);

  const literatureReview = useMemo(() => {
    return data;
  }, [data]);

  return { literatureReview, ...rest };
};

export const useSearchPapersService = () => {
  const { mutate: searchPapers, ...rest } = useSearchPapers();

  return { searchPapers, ...rest };
};

export const useCreateLiteratureReviewService = () => {
  const { mutate: createLiteratureReview, ...rest } =
    useCreateLiteratureReview();

  return { createLiteratureReview, ...rest };
};

export const useUpdateLiteratureReviewService = () => {
  const {
    mutate: updateLiteratureReview,
    isPending: isUpdateLiteratureReviewPending,
    ...rest
  } = useUpdateLiteratureReview();

  return { updateLiteratureReview, isUpdateLiteratureReviewPending, ...rest };
};

export const useGetAbstractScreeningByIdService = (id: string | undefined) => {
  const { data: abstractScreening, ...rest } =
    useGetAbstractScreeningByIdApi(id);

  return { abstractScreening, ...rest };
};

export const useCreateAbstractScreeningService = () => {
  const { mutate: createAbstractScreening, ...rest } =
    useCreateAbstractScreening();

  return { createAbstractScreening, ...rest };
};

export const useExecuteAbstractScreeningService = () => {
  const {
    mutate: executeAbstractScreening,
    isPending: isExecuteAbstractScreeningPending,
    ...rest
  } = useExecuteAbstractScreening();

  return {
    executeAbstractScreening,
    isExecuteAbstractScreeningPending,
    ...rest,
  };
};

export const useGetAbstractScreeningResultsByIdPollingService = (
  id: string | undefined,
  filters?: GetAbstractScreeningResultsFilterParams
) => {
  const { data, ...rest } = useGetAbstractScreeningResultsByIdApi(id, filters, {
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return false;
      if (data.isGenerating) return 1000;
      return false;
    },
  });

  const isAbstractScreeningGenerating = useMemo(() => {
    return data?.isGenerating ?? false;
  }, [data]);

  const paperScreeningResults = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  const resultsStats = data?.resultsStats;
  const totalCounts = data?.totalCounts;

  return {
    paperScreeningResults,
    isAbstractScreeningGenerating,
    resultsStats,
    totalCounts,
    ...rest,
  };
};

export const useCreateAndExecuteLiteratureReviewSearchesService = () => {
  const { mutate: createAndExecuteSearches, ...rest } =
    useCreateAndExecuteLiteratureReviewSearches();

  return { createAndExecuteSearches, ...rest };
};

export const useSearchResultsService = () => {
  const getSearchResults = (literatureReviewId: string) => {
    return useGetSearchResultsApi(literatureReviewId);
  };

  return {
    getSearchResults,
  };
};

export const useLiteratureSearchesService = () => {
  const getLiteratureSearches = (literatureReviewId: string) => {
    return useGetLiteratureSearchesApi(literatureReviewId);
  };

  return {
    getLiteratureSearches,
  };
};

export const useExportLiteratureReviewService = (
  literatureReviewId: string | undefined
) => {
  const { mutate, ...rest } = useExportLiteratureReviewApi(literatureReviewId);

  const exportLiteratureReview = (format: 'xlsx' | 'csv') =>
    mutate(format, {
      onSuccess: data => {
        if (data) {
          browserDownload(data.signedUrl, `literature-review.${format}`);
        }
      },
    });

  return { exportLiteratureReview, ...rest };
};

export const useUpdatePaperScreeningResultService = () => {
  const { mutate: updatePaperScreeningResult, ...rest } =
    useUpdatePaperScreeningResultApi();

  return { updatePaperScreeningResult, ...rest };
};

export const useUpdatePaperScreeningValidationStatusService = () => {
  const { mutate: updatePaperScreeningValidationStatus, ...rest } =
    useUpdatePaperScreeningValidationStatusApi();

  return { updatePaperScreeningValidationStatus, ...rest };
};

export const useBulkValidatePaperScreeningsService = () => {
  const {
    mutate: bulkValidatePaperScreenings,
    isPending,
    ...rest
  } = useBulkValidatePaperScreeningsApi();

  return {
    bulkValidatePaperScreenings: async (paperScreeningIds: string[]) => {
      return new Promise<void>((resolve, reject) => {
        bulkValidatePaperScreenings(paperScreeningIds, {
          onSuccess: () => resolve(),
          onError: error => reject(error),
        });
      });
    },
    isPending,
    ...rest,
  };
};

export const useUpdatePaperScreeningResultJustificationService = () => {
  const { mutate: updatePaperScreeningResultJustification, ...rest } =
    useUpdatePaperScreeningResultJustificationApi();

  return { updatePaperScreeningResultJustification, ...rest };
};

export const useLiteratureReviewSearchesService = (
  literatureReviewId: string | undefined
) => {
  const { data, ...rest } = useLiteratureReviewSearchesApi(literatureReviewId);

  const completeResults = useMemo(() => {
    return data?.completeResults ?? [];
  }, [data]);

  const literatureReviewSearches = useMemo(() => {
    return data?.searches ?? [];
  }, [data]);

  const stats = useMemo(() => {
    return data?.stats ?? { total: 0, duplicates: 0, invalid: 0 };
  }, [data]);

  return { literatureReviewSearches, completeResults, stats, ...rest };
};

export const useUpsertAndExecuteLiteratureReviewSearchesService = (
  literatureReviewId: string | undefined
) => {
  const { mutate: upsertAndExecuteLiteratureReviewSearches, ...rest } =
    useUpsertAndExecuteLiteratureReviewSearchesApi(literatureReviewId);

  return { upsertAndExecuteLiteratureReviewSearches, ...rest };
};

export const useDeleteLiteratureSearchService = (
  literatureReviewId: string | undefined
) => {
  const { mutate: deleteLiteratureSearch, ...rest } =
    useDeleteLiteratureSearchApi(literatureReviewId);

  return { deleteLiteratureSearch, ...rest };
};

export const useExportLiteratureSearchesService = (
  literatureReviewId: string | undefined
) => {
  const {
    mutate,
    isPending: isExportPending,
    ...rest
  } = useExportLiteratureSearchesApi(literatureReviewId);

  const exportLiteratureSearches = (
    options?: MutateOptions<ExportLiteratureSearchesResponse>
  ) =>
    mutate(undefined, {
      onSuccess: data => {
        if (data) {
          browserDownload(data.signedUrl, `literature-review-searches.xlsx`);
        }
      },
      ...options,
    });

  return { exportLiteratureSearches, isExportPending, ...rest };
};

export const useGenerateCriteriaService = () => {
  const {
    mutate: generateCriteria,
    isPending: isGenerating,
    ...rest
  } = useGenerateCriteriaApi();

  return { generateCriteria, isGenerating, ...rest };
};
export const useCreateCriteriaService = () => {
  const { mutate: createCriteria, ...rest } = useCreateCriteriaApi();

  return { createCriteria, ...rest };
};

export const useUpdateCriteriaService = () => {
  const { mutate: updateCriteria, ...rest } = useUpdateCriteriaApi();

  return { updateCriteria, ...rest };
};

export const useDeleteCriteriaService = () => {
  const { mutate: deleteCriteria, ...rest } = useDeleteCriteriaApi();

  return { deleteCriteria, ...rest };
};

export const useGenerateFullTextCriteriaService = () => {
  const {
    mutate: generateFullTextCriteria,
    isPending: isGenerateFullTextCriteriaPending,
    ...rest
  } = useGenerateFullTextCriteriaApi();

  return {
    generateFullTextCriteria,
    isGenerateFullTextCriteriaPending,
    ...rest,
  };
};

export const useUpsertFullTextScreeningService = () => {
  const {
    mutate: upsertFullTextScreening,
    isPending: isUpsertFullTextScreeningPending,
    ...rest
  } = useUpsertFullTextScreeningApi();

  return { upsertFullTextScreening, isUpsertFullTextScreeningPending, ...rest };
};

export const useGetFullTextScreeningService = (
  literatureReviewId: string | undefined
) => {
  const { data: fullTextScreening, ...rest } =
    useGetFullTextScreeningApi(literatureReviewId);

  return { fullTextScreening, ...rest };
};

export const useGetFullTextScreeningResultsService = (
  literatureReviewId: string | undefined,
  filters?: GetFullTextScreeningResultsFilterParams
) => {
  const { data, ...rest } = useGetFullTextScreeningResultsApi(
    literatureReviewId,
    filters,
    {
      refetchInterval: query => {
        const data = query.state.data;
        if (!data) return false;
        if (data.isGenerating) return 1000;
        return false;
      },
    }
  );

  const fullTextScreeningResults = useMemo(() => {
    return data?.data ?? [];
  }, [data]);

  const isFullTextScreeningGenerating = useMemo(() => {
    return data?.isGenerating ?? false;
  }, [data]);

  const resultsStats = data?.resultsStats;
  const totalCounts = data?.totalCounts;

  return {
    fullTextScreeningResults,
    isFullTextScreeningGenerating,
    resultsStats,
    totalCounts,
    ...rest,
  };
};

export const useExportFullTextScreeningResultsService = (
  literatureReviewId: string | undefined
) => {
  const { mutate, ...rest } =
    useExportFullTextScreeningResultsApi(literatureReviewId);

  const exportFullTextScreeningResults = (format: 'xlsx' | 'csv') =>
    mutate(format, {
      onSuccess: data => {
        if (data) {
          browserDownload(
            data.signedUrl,
            `full-text-screening-results.${format}`
          );
        }
      },
    });

  return { exportFullTextScreeningResults, ...rest };
};

export const useExecuteFullTextScreeningService = () => {
  const {
    mutate: executeFullTextScreening,
    isPending: isExecuteFullTextScreeningPending,
    ...rest
  } = useExecuteFullTextScreeningApi();

  return {
    executeFullTextScreening,
    isExecuteFullTextScreeningPending,
    ...rest,
  };
};

export const useUpdateFullTextScreeningCriteriaResultService = () => {
  const { mutate: updateFullTextScreeningCriteriaResult, ...rest } =
    useUpdateFullTextScreeningCriteriaResultApi();

  return { updateFullTextScreeningCriteriaResult, ...rest };
};

export const useUploadWorkPdfService = () => {
  const { mutate: uploadWorkPdf, isPending, ...rest } = useUploadWorkPdfApi();

  return { uploadWorkPdf, isUploading: isPending, ...rest };
};
